import { useCallback, useEffect, useRef } from 'react';
import { LogLevel, QueuedLog } from '../types';

interface UseOfflineRetryProps {
  isOnline: boolean;
  maxRetries: number;
  retryDelay: number;
  processLog: (level: LogLevel, message: string, context?: Record<string, unknown>) => void;
}

export const useOfflineRetry = ({ isOnline, maxRetries, retryDelay, processLog }: UseOfflineRetryProps) => {
  const queuedLogs = useRef<QueuedLog[]>([]);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const retryQueuedLogs = useCallback(() => {
    if (!isOnline || queuedLogs.current.length === 0) return;

    const currentLogs = [...queuedLogs.current];
    queuedLogs.current = [];

    currentLogs.forEach((log) => {
      // Attempt to send the log
      try {
        processLog(log.level, log.message, log.context);
      } catch (error) {
        console.warn('Failed to process queued log:', error);
        // If failed and not at max retries, increment retry count and re-queue
        if (log.retryCount < maxRetries) {
          queuedLogs.current.push({
            ...log,
            retryCount: log.retryCount + 1
          });
        } else {
          // Log is dropped after max retries
          console.warn('Log dropped after max retries:', log);
        }
      }
    });

    // Schedule next retry if there are still queued logs
    if (queuedLogs.current.length > 0) {
      retryTimeoutRef.current = setTimeout(retryQueuedLogs, retryDelay);
    }
  }, [isOnline, maxRetries, retryDelay, processLog]);

  const queueLog = useCallback(
    (level: LogLevel, message: string, context?: Record<string, unknown>) => {
      if (isOnline) {
        // If we're online, try to process immediately
        try {
          processLog(level, message, context);
        } catch (error) {
          console.warn('Failed to process log immediately:', error);
          // If immediate processing fails, queue with retry
          queuedLogs.current.push({
            level,
            message,
            timestamp: Date.now(),
            context,
            retryCount: 0
          });
          // Start retry process
          retryQueuedLogs();
        }
      } else {
        // If offline, just queue the log
        queuedLogs.current.push({
          level,
          message,
          timestamp: Date.now(),
          context,
          retryCount: 0
        });
      }
    },
    [isOnline, processLog, retryQueuedLogs]
  );

  // Attempt to process queued logs when coming back online
  useEffect(() => {
    if (isOnline) {
      retryQueuedLogs();
    }
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [isOnline, retryQueuedLogs]);

  return { queueLog };
};
